import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../lib/auth.js';
import { LeadCelebrationToast, type LeadCreatedEvent } from './LeadCelebrationToast.js';
import { AiQualificationAlertToast, type AiQualificationAlertEvent } from './AiQualificationAlertToast.js';

const CELEBRATION_AUTO_DISMISS_MS = 8000;
const AI_ALERT_AUTO_DISMISS_MS = 12000;

function isLeadCreatedEvent(data: unknown): data is LeadCreatedEvent {
  return typeof data === 'object' && data !== null && (data as { type?: unknown }).type === 'lead.created';
}

function isAiQualificationAlertEvent(data: unknown): data is AiQualificationAlertEvent {
  return typeof data === 'object' && data !== null && (data as { type?: unknown }).type === 'ai_qualification.alert';
}

/**
 * Opens the one `GET /api/v1/realtime/stream` SSE connection per logged-in
 * tab (`RealtimeController`) and reacts to events on it — the "new lead"
 * celebration and the "AI Lead Review needs you" alert (the latter gated
 * on `ai.review_queue.view`, since it's broadcast tenant-wide with no
 * server-side recipient filtering) — invalidating the notification
 * queries so the bell's count updates live too (it otherwise only
 * refetches on window refocus, `lib/auth.tsx`'s `sessionQueryOptions`
 * neighbor). Mounted inside `AuthProvider` in `main.tsx` since it needs
 * `useAuth()` to know whether a
 * session exists at all — there's nothing to stream for a logged-out tab,
 * and `EventSource` would just retry against a 401 forever.
 *
 * One celebration shown at a time: a second `lead.created` arriving while
 * one is already up replaces it rather than stacking, since this is a
 * brief "heads up" moment, not a queue someone needs to read every entry
 * of (that's what the bell/notification list is for).
 */
export function RealtimeProvider({ children }: { children: ReactNode }) {
  const { session, hasPermission } = useAuth();
  const queryClient = useQueryClient();
  const [celebration, setCelebration] = useState<LeadCreatedEvent | null>(null);
  const dismissTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [aiAlert, setAiAlert] = useState<AiQualificationAlertEvent | null>(null);
  const aiAlertDismissTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (import.meta.env.DEV && new URLSearchParams(window.location.search).get('__previewCelebration')) {
      setCelebration({ type: 'lead.created', leadId: '00000000-0000-0000-0000-000000000401', leadName: 'Priya Shah', companyName: 'Harbor Logistics', sourceName: 'Claude', createdByName: 'Sofia Reyes' });
    }
  }, []);

  useEffect(() => {
    if (!session) return;
    // The `?__design=<Screen>` fixture harness (main.tsx) mocks `fetch`, not
    // `EventSource` — without this guard every design-preview screen would
    // open a real SSE connection against whatever's listening on :5173,
    // which either errors immediately or (if some other backend happens to
    // be up) streams events design fixtures were never meant to react to.
    if (import.meta.env.DEV && new URLSearchParams(window.location.search).get('__design')) return;

    const source = new EventSource('/api/v1/realtime/stream', { withCredentials: true });
    source.addEventListener('message', (raw) => {
      let data: unknown;
      try {
        data = JSON.parse((raw as MessageEvent<string>).data);
      } catch {
        return;
      }
      if (isLeadCreatedEvent(data)) {
        if (dismissTimer.current) clearTimeout(dismissTimer.current);
        setCelebration(data);
        dismissTimer.current = setTimeout(() => setCelebration(null), CELEBRATION_AUTO_DISMISS_MS);
      } else if (isAiQualificationAlertEvent(data) && hasPermission('ai.review_queue.view')) {
        if (aiAlertDismissTimer.current) clearTimeout(aiAlertDismissTimer.current);
        setAiAlert(data);
        aiAlertDismissTimer.current = setTimeout(() => setAiAlert(null), AI_ALERT_AUTO_DISMISS_MS);
        queryClient.invalidateQueries({ queryKey: ['ai-lead-qualifications'] });
      } else {
        return;
      }

      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['notifications-unread-count'] });
    });

    return () => {
      source.close();
      if (dismissTimer.current) clearTimeout(dismissTimer.current);
      if (aiAlertDismissTimer.current) clearTimeout(aiAlertDismissTimer.current);
    };
  }, [session, queryClient, hasPermission]);

  return (
    <>
      {children}
      {celebration && (
        <LeadCelebrationToast
          event={celebration}
          onDismiss={() => {
            if (dismissTimer.current) clearTimeout(dismissTimer.current);
            setCelebration(null);
          }}
        />
      )}
      {aiAlert && (
        <AiQualificationAlertToast
          event={aiAlert}
          onDismiss={() => {
            if (aiAlertDismissTimer.current) clearTimeout(aiAlertDismissTimer.current);
            setAiAlert(null);
          }}
        />
      )}
    </>
  );
}
